
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Star, BarChart3, LineChart, TrendingUp, Activity, PieChart, Calendar, Clock, Layers, Table, Percent, DollarSign } from 'lucide-react';
import { WIDGET_REGISTRY, WIDGET_CATEGORIES } from '../../config/widgetRegistry';
import { useClickAway } from '../../hooks/useClickAway';
import { filterEnabledWidgetIds } from '../../config/runtime/widgetGates';
import { currentRuntime } from '../../config/runtime';

type Props = {
  theme?: 'light' | 'dark';
  widgetConfig: Record<string, { visible: boolean; size?: string; title?: string }>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  className?: string;
  favorites?: string[];
  onFavoritesChange?: (favs: string[]) => void;
  /** Controlled open state (optional). If provided, launcher becomes controllable. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When provided, launcher will call this instead of onToggle to allow the caller to place a widget into a specific section */
  onSelectWidget?: (id: string, targetSectionId?: string | number | null) => void;
  /** When provided and targetSectionId is set, Apply will replace the section's widgetIds with this selection (supports clearing). */
  onApplySectionWidgets?: (targetSectionId: string | number, widgetIds: string[]) => void;
  /** Optional section id that indicates the launcher was opened to add into that section */
  targetSectionId?: string | number | null;
  /** Visible widgets for the targeted section so checkboxes prefill correctly */
  targetSectionWidgetIds?: string[];
  /** When true, don't render the visual trigger button (menu can still open programmatically) */
  hideTrigger?: boolean;
  /** Optional anchor element used when opening programmatically (e.g., from another button) */
  anchorRef?: React.RefObject<HTMLElement>;
};

const FAV_KEY = 'dashboard:widget-favorites';


function useFavorites(externalFavorites?: string[], onFavoritesChange?: (favs: string[]) => void) {
  const isControlled = Array.isArray(externalFavorites);
  const [internalFavs, setInternalFavs] = useState<string[]>(() => {
    if (isControlled) return externalFavorites as string[];
    try {
      const raw = localStorage.getItem(FAV_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  });

  // If controlled, always reflect externalFavorites; otherwise persist internal to localStorage
  useEffect(() => {
    if (isControlled) return;
    try { localStorage.setItem(FAV_KEY, JSON.stringify(internalFavs)); } catch {}
  }, [internalFavs, isControlled]);

  useEffect(() => {
    if (isControlled) {
      // keep internalFavs in sync for rendering convenience but don't trigger change callbacks
      setInternalFavs(externalFavorites as string[]);
    }
  }, [externalFavorites, isControlled]);

  const toggleFav = (id: string) => {
    if (isControlled) {
      const next = (externalFavorites as string[]).includes(id)
        ? (externalFavorites as string[]).filter(x => x !== id)
        : [...(externalFavorites as string[]), id];
      onFavoritesChange?.(next);
      return;
    }
    setInternalFavs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const clearFavs = () => {
    if (isControlled) {
      onFavoritesChange?.([]);
      return;
    }
    setInternalFavs([]);
  };

  const favorites = isControlled ? (externalFavorites as string[]) : internalFavs;
  return { favorites, toggleFav, clearFavs };
}

export default function WidgetsLauncher({ theme, widgetConfig, onToggle, onSelectAll, onClearAll, className = '', favorites: externalFavorites, onFavoritesChange, open: openProp, onOpenChange, onSelectWidget, onApplySectionWidgets, targetSectionId, targetSectionWidgetIds, hideTrigger, anchorRef }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? (openProp as boolean) : internalOpen;
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Consider clicks inside either the button or the portaled menu as internal
  useClickAway([btnRef, menuRef], () => { if (controlled) { onOpenChange?.(false); } else { setInternalOpen(false); } exitPending(); });
  const { favorites, toggleFav, clearFavs } = useFavorites(externalFavorites, onFavoritesChange);
  // Determine theme each render: prefer prop; fallback to documentElement
  const isDark = (theme ?? (typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light')) === 'dark';

  // Build grouped widget lists from registry
  const items = useMemo(() => {
    return filterEnabledWidgetIds(Object.keys(WIDGET_REGISTRY), currentRuntime.features).map((key) => {
      const def = WIDGET_REGISTRY[key];
      return { id: key, title: def.title, category: def.category };
    });
  }, []);

  // Local pending selection state: when non-null, header actions operate on this set
  const [pendingVisible, setPendingVisible] = useState<Set<string> | null>(null);

  const grouped = useMemo(() => {
    // Override widget definitions with EXACT 4 categories from user request
    const MANUAL_CATEGORIES = {
      "Performance Analysis": [
        "performanceByDay", "performanceByDuration", 
        "performanceByEntryAtr", "performanceByInTradePriceRange", 
        "performanceByMonth", "performanceByMovement", 
        "performanceByOpeningGap", "performanceByPrice", 
        "performanceByRelativeVolatility", "performanceByAtr", 
        "performanceByTimeOfDay", "performanceByVolume", 
        "performanceByVolumeTraded"
      ],
      "Averages": [
        "averageDailyGainLoss", "averagePerShareGainLoss", 
        "averageTradePnl", "avgWinLoss"
      ],
      "Totals & Cumulative": [
        "cumulativeDrawdown", "cumulativePnl", 
        "totalCommissions", "totalFees", "totalGainLoss"
      ],
      "Trade Statistics": [
        "dailyVolume", "holdTimeAnalysis", "largestGain", 
        "largestGainLoss", "largestLoss", "longTermInvestments", 
        "maxConsecutiveLosses", "maxConsecutiveWins", "numberOfLosingTrades", 
        "numberOfWinningTrades", "profitFactor", "openTrades", 
        "tradePlStandardDeviation", "winningVsLosing"
      ]
    };

    const map = new Map<string, { id: string; title: string }[]>();
    for (const [catName, widgetIds] of Object.entries(MANUAL_CATEGORIES)) {
      map.set(catName, []);
      for (const wid of widgetIds) {
        const itemDef = items.find(i => i.id === wid);
        if (itemDef) {
          map.get(catName)!.push({ id: itemDef.id, title: itemDef.title });
        } else {
          // Skip widgets that are not present in the enabled items list (e.g., feature-gated)
          continue;
        }
      }
    }
    return map;
  }, [items]);

  const categories = useMemo(() => Array.from(grouped.keys()), [grouped]);

  const visibleIds = useMemo(() => {
    return new Set(
      Object.keys(widgetConfig).filter((id) => {
        return widgetConfig[id]?.visible;
      })
    );
  }, [widgetConfig]);

  const targetSectionVisibleIds = useMemo(() => new Set(targetSectionWidgetIds || []), [targetSectionWidgetIds]);

  const allIds = useMemo(() => items.map(i => i.id), [items]);

  const enterPending = (initial: string[] = []) => {
    setPendingVisible(new Set(initial));
  };

  const exitPending = () => setPendingVisible(null);

  // applyPending removed: Clear/Select operate on UI-only pending set; pending is discarded when menu closes

  const effectiveIds = useMemo(() => allIds.filter(id => id !== 'weekSummary'), [allIds]);
  const selectedCount = pendingVisible ? (pendingVisible.size - (pendingVisible.has('weekSummary') ? 1 : 0)) : (visibleIds.size - (visibleIds.has('weekSummary') ? 1 : 0));

  const allSelected = useMemo(() => {
    return effectiveIds.length > 0 && effectiveIds.every(id => pendingVisible ? pendingVisible.has(id) : visibleIds.has(id));
  }, [effectiveIds, pendingVisible, visibleIds]);

  const noneSelected = useMemo(() => {
    return effectiveIds.length > 0 && effectiveIds.every(id => !(pendingVisible ? pendingVisible.has(id) : visibleIds.has(id)));
  }, [effectiveIds, pendingVisible, visibleIds]);

  // Icon map for widgets; fallback to BarChart3 if unknown
  const ICON_MAP: Record<string, any> = {
    cumulativePnl: LineChart,
    cumulativeDrawdown: LineChart,
    winRate: Percent,
    winningVsLosing: PieChart,
    dailyVolume: BarChart3,
    averageTradePnl: DollarSign,
    avgWinLoss: TrendingUp,
    totalFees: DollarSign,
    profitFactor: Activity,
    maxConsecutiveWins: Activity,
    maxConsecutiveLosses: Activity,
    averagePerShareGainLoss: DollarSign,
    totalGainLoss: DollarSign,
    tradePlStandardDeviation: Activity,
    averageDailyGainLoss: DollarSign,
    totalCommissions: DollarSign,
    numberOfWinningTrades: BarChart3,
    largestGain: TrendingUp,
    averageHoldTimeLosingTrades: Clock,
    largestLoss: Activity,
    holdTimeAnalysis: Clock,
    // averageTradeGainLoss removed
    numberOfLosingTrades: BarChart3,
    openTrades: Table,
    recentTrades: Table,
    performanceByTimeOfDay: Clock,
    performanceByRelativeVolatility: Activity,
    performanceByVolumeTraded: BarChart3,
    performanceByEntryAtr: Activity,
    performanceByInTradePriceRange: Layers,
    performanceByDuration: Clock,
    performanceByDay: Calendar,
    performanceByPrice: BarChart3,
    performanceByOpeningGap: Activity,
    performanceByVolume: BarChart3,
    performanceByMonth: Calendar,
    performanceByAtr: Activity,
    performanceByMovement: Activity,
  };

  // Short descriptions; fallback to category description if missing
  const WIDGET_DESCRIPTIONS: Record<string, string> = {
    cumulativePnl: 'Cumulative sum of realized P&L over time.',
    cumulativeDrawdown: 'Running peak-to-trough drawdown.',
    winRate: 'Percent of non-breakeven trades that closed green.',
    winningVsLosing: 'Distribution of winners vs losers.',
    dailyVolume: 'Aggregate traded volume by day.',
    averageTradePnl: 'Average profit or loss per trade.',
    avgWinLoss: 'Average winner size vs average loser.',
    profitFactor: 'Gross profits divided by gross losses.',
    maxConsecutiveWins: 'Longest win streak.',
    maxConsecutiveLosses: 'Longest losing streak.',
    averageDailyGainLoss: 'Mean daily P&L.',
    totalCommissions: 'Total commissions paid.',
    totalFees: 'All fees across selected range.',
    totalGainLoss: 'Total net P&L across period.',
    tradePlStandardDeviation: 'Std dev of trade P&L.',
    numberOfWinningTrades: 'Count of green trades.',
    numberOfLosingTrades: 'Count of red trades.',
    openTrades: 'Currently open positions.',
    recentTrades: 'Most recent closed trades.',
    performanceByTimeOfDay: 'Outcomes across intraday time buckets.',
    performanceByRelativeVolatility: 'Performance by relative volatility bands.',
    performanceByVolumeTraded: 'Performance by shares/contracts traded.',
    performanceByEntryAtr: 'Outcomes by entry expressed in ATR.',
    performanceByInTradePriceRange: 'Performance across in-trade price ranges.',
    performanceByDuration: 'Outcomes by holding duration.',
    performanceByDay: 'Day-of-week performance breakdown.',
    performanceByPrice: 'Outcomes by price buckets.',
    performanceByOpeningGap: 'Performance by opening gap size.',
    performanceByVolume: 'Outcomes by traded volume buckets.',
    performanceByMonth: 'Month-of-year performance breakdown.',
    performanceByAtr: 'Symbol performance vs ATR.',
    performanceByMovement: 'Outcomes by movement magnitude.',
    holdTimeAnalysis: 'Holding time distribution; winners vs losers.',
  };

  const favButtons = useCallback((id: string) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); toggleFav(id); }}
      className={`absolute -top-2 -right-2 rounded-full border transition-colors inline-flex items-center justify-center w-7 h-7 shadow-sm ${
        favorites.includes(id)
          ? (isDark ? 'bg-amber-600 border-amber-500' : 'bg-amber-500 border-amber-400')
          : (isDark ? 'bg-gray-700 border-gray-600' : 'bg-zinc-200 border-zinc-300')
      }`}
      aria-label={favorites.includes(id) ? 'Remove from favorites' : 'Add to favorites'}
      title={favorites.includes(id) ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star className={`w-3.5 h-3.5 ${favorites.includes(id) ? 'text-amber-200' : (isDark ? 'text-gray-300' : 'text-zinc-300')}`} fill={favorites.includes(id) ? 'currentColor' : undefined} />
    </button>
  ), [favorites, isDark, toggleFav]);

  const headerText = 'text-zinc-700 dark:text-gray-300';
  const subtleText = 'text-zinc-500 dark:text-gray-500';
  const borderCol = 'border-zinc-300 dark:border-gray-700';
  const panelBg = 'bg-white dark:bg-gray-950';

  const clearBtnClass = `text-sm px-3 py-2 rounded-md border ${isDark ? 'text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 border-amber-600/40' : 'text-amber-700 bg-amber-100 hover:bg-amber-200 border-amber-400/40'}`;

  // Favorites-specific chip styling: selected uses amber instead of blue
  const favChip = (on: boolean) => isDark
    ? on
      ? 'bg-amber-900/20 border-amber-500 text-amber-100'
      : 'bg-zinc-900 border-gray-700 text-zinc-300'
    : on
      ? 'bg-amber-50 border-amber-400 text-amber-900'
      : 'bg-white border-zinc-300 text-zinc-900';

  const handleSelectAll = () => { onSelectAll(); setPendingVisible(null); };
  const handleClearAll = () => { onClearAll(); setPendingVisible(null); };

  // Normalize titles to Title Case without ALL CAPS; preserve common acronyms
  function normalizeTitle(title: string): string {
    if (!title) return title;
    // Lowercase then title-case words, but preserve specific acronyms
    const ACRONYMS = new Set(['P&L', 'ATR']);
    const cleaned = title
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(word => {
        // Keep punctuation like % and &
        const base = word.replace(/[^A-Za-z&%]/g, '')
          .toUpperCase();
        if (ACRONYMS.has(base)) return base;
        // Title case otherwise
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
    return cleaned;
  }

  // Category header gradients + descriptions to match screenshot vibe
  const CATEGORY_STYLES: Record<string, { from: string; to: string; iconColor: string }> = {
    "Performance Analysis": { from: 'from-indigo-500/15', to: 'to-indigo-400/5', iconColor: 'text-indigo-500' },
    "Averages": { from: 'from-sky-500/15', to: 'to-sky-400/5', iconColor: 'text-sky-500' },
    "Totals & Cumulative": { from: 'from-emerald-600/15', to: 'to-emerald-600/5', iconColor: 'text-emerald-600' },
    "Trade Statistics": { from: 'from-amber-500/15', to: 'to-amber-400/5', iconColor: 'text-amber-500' },
    performance: { from: 'from-indigo-500/15', to: 'to-indigo-400/5', iconColor: 'text-indigo-500' },
    charts:      { from: 'from-sky-500/15',    to: 'to-sky-400/5',    iconColor: 'text-sky-500' },
    statistics:  { from: 'from-emerald-600/15',to: 'to-emerald-600/5', iconColor: 'text-emerald-600' },
    tables:      { from: 'from-red-500/15',   to: 'to-red-500/5',   iconColor: 'text-red-500' },
    risk:        { from: 'from-fuchsia-500/15',to: 'to-fuchsia-400/5',iconColor: 'text-fuchsia-500' },
    portfolio:   { from: 'from-amber-500/15',  to: 'to-amber-400/5',  iconColor: 'text-amber-500' },
    market:      { from: 'from-cyan-500/15',   to: 'to-cyan-400/5',   iconColor: 'text-cyan-500' },
    execution:   { from: 'from-violet-500/15', to: 'to-violet-400/5', iconColor: 'text-violet-500' },
  };
  const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    "Performance Analysis": 'Conditional outcomes by regime, buckets or segments.',
    "Averages": 'Mean performance metrics and expected values.',
    "Totals & Cumulative": 'Running counts, balances and aggregated expenses.',
    "Trade Statistics": 'Rates, streaks, lengths, and summary tracking.',
    performance: 'Conditional outcomes by regime, buckets or segments.',
    charts: 'Time or distribution visualizations of key metrics.',
    statistics: 'Rates, streaks and summary payoff ratios.',
    tables: 'Detailed lists such as open or recent trades.',
    risk: 'Volatility, drawdowns and ATR-relative measures.',
    portfolio: 'Portfolio-level aggregations and exposures.',
    market: 'Contextual market features and regimes.',
    execution: 'Execution quality and microstructure metrics.',
  };

  // Fallback: get a generic description from category if a widget lacks one
  function descFromCategory(catKey: string): string {
    return CATEGORY_DESCRIPTIONS[catKey] || '';
  }

  // Positioning for portaled menu
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = () => {
    const anchor = btnRef.current ?? anchorRef?.current;
    const margin = 16;
    const offset = 8;
    if (!anchor) return;

    // If launcher was opened for a specific section, attach under the separator
    if (typeof targetSectionId !== 'undefined' && targetSectionId !== null) {
      try {
        const anchorEl = document.querySelector(`[data-section-anchor="${String(targetSectionId)}"]`) as HTMLElement | null;
        const contentEl = document.querySelector(`[data-section-id="${String(targetSectionId)}"]`) as HTMLElement | null;
        // prefer contentEl for width/left so panel lines up with section body
        const contentRect = contentEl ? contentEl.getBoundingClientRect() : null;
        const anchorRect = anchorEl ? anchorEl.getBoundingClientRect() : contentRect;
        if (anchorRect && contentRect) {
          const panelMaxH = Math.min(900, Math.max(420, window.innerHeight - 48));
          setMenuStyle({ position: 'relative', top: 'auto', left: 'auto', width: '100%', maxWidth: '1400px', maxHeight: panelMaxH, zIndex: 9999 });
          return;
        }
      } catch {}
    }

    // Default: anchor near the button (top-bar)
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const menuWidth = 880;
    const menuMaxDefault = 700;
    // Right-align the menu to the button's right edge and clamp to viewport
    let left = Math.round(rect.right - menuWidth);
    if (left < margin) left = margin;
    if (left + menuWidth > viewportWidth - margin) left = Math.max(margin, viewportWidth - margin - menuWidth);

    const availBelow = Math.max(0, window.innerHeight - rect.bottom - margin - offset);
    const availAbove = Math.max(0, rect.top - margin - offset);
    let top = Math.round(rect.bottom + offset);
    let inlineMaxHeight = Math.min(menuMaxDefault, availBelow || menuMaxDefault);

    if (availBelow < 160 && availAbove > availBelow) {
      inlineMaxHeight = Math.min(menuMaxDefault, availAbove);
      top = Math.round(rect.top - offset - inlineMaxHeight);
    }

    if (availBelow < 80 && availAbove < 80) {
      top = Math.round(rect.bottom + offset);
      inlineMaxHeight = Math.max(120, Math.min(menuMaxDefault, availBelow));
    }

    setMenuStyle({ position: 'fixed', top, left, width: menuWidth, maxWidth: '96vw', maxHeight: inlineMaxHeight, zIndex: 9999 });
  };

  // If the launcher is opened programmatically (hideTrigger=true) and there is no trigger button,
  // compute a sensible default position near the top-right of the viewport so the menu is visible.
  const ensureMenuPositionFallback = () => {
    if (btnRef.current || anchorRef?.current) return;
    // If targetSectionId handled above, updateMenuPosition would have returned earlier. Here we handle default.
    const margin = 16;
    const menuWidth = 880;
    const viewportWidth = window.innerWidth;
    const left = Math.max(margin, viewportWidth - margin - menuWidth);
    const top = 64; // place below header area
    const inlineMaxHeight = Math.min(700, window.innerHeight - top - margin);
    setMenuStyle({ position: 'fixed', top, left, width: menuWidth, maxWidth: '96vw', maxHeight: inlineMaxHeight, zIndex: 9999 });
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const raf = requestAnimationFrame(() => updateMenuPosition());
    const t = setTimeout(() => updateMenuPosition(), 0);
    window.addEventListener('resize', updateMenuPosition);
    const addedScroll = !(typeof targetSectionId !== 'undefined' && targetSectionId !== null);
    if (addedScroll) window.addEventListener('scroll', updateMenuPosition, true);
    // If no visible trigger exists (hideTrigger=true), ensure we fallback to a visible position
    if (!btnRef.current) ensureMenuPositionFallback();
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      if (addedScroll) window.removeEventListener('scroll', updateMenuPosition, true);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetSectionId]);

  // When opening for a section, pre-populate the pendingVisible set from current visibleIds
  useEffect(() => {
    if (!open) return;
    if (typeof targetSectionId !== 'undefined' && targetSectionId !== null) {
      // If `targetSectionWidgetIds` prop was provided (even if empty), respect it.
      // Otherwise fall back to global visibleIds.
      if (typeof targetSectionWidgetIds !== 'undefined') {
        enterPending(Array.from(targetSectionVisibleIds));
      } else {
        enterPending(Array.from(visibleIds));
      }
    }
  }, [open, targetSectionId, visibleIds, targetSectionVisibleIds, targetSectionWidgetIds]);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {!hideTrigger && (
        <button
          ref={btnRef}
          className={`px-4 h-9 rounded-xl border text-sm transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${open ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
          onClick={() => { if (controlled) { onOpenChange?.(!open); } else { setInternalOpen(prev => !prev); } }}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          Widgets
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
      {open && createPortal(
        (typeof targetSectionId !== 'undefined' && targetSectionId !== null) ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[6px] px-4 py-6">
            <div
              ref={menuRef}
              role="menu"
              className="relative w-full max-w-[1400px] max-h-[calc(100vh-3rem)] bg-gradient-to-b from-[#0F131C] to-[#0A0C10] border border-[#1E2330] rounded-lg flex flex-col shadow-[0_0_0_1px_rgba(255,255,255,0.02)_inset,0_30px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden animate-in fade-in zoom-in-[0.98] duration-300 ease-out"
              style={menuStyle}
            >
              <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #1E2330; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #2A3143; }
              `}} />
              
              {/* Top Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-600 via-teal-400 to-emerald-400 z-50"></div>

              {/* Header Section */}
              <div className="px-6 py-4 bg-zinc-900 border-b border-[#1E2330] flex items-center justify-between relative shadow-[0_4px_20px_rgba(0,0,0,0.6)] z-20 shrink-0">
              
              <div className="relative z-10 w-32">
                <button 
                  onClick={(e) => { e.stopPropagation(); handleSelectAll(); }}
                  disabled={allSelected}
                  className="px-4 py-1.5 text-xs font-medium text-slate-400 hover:text-white border border-[#2A3143] hover:border-slate-500 hover:bg-white/5 rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A3143] disabled:cursor-not-allowed"
                >
                  SELECT ALL
                </button>
              </div>
              
              {/* Title label */}
              <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3.5 z-10 pointer-events-none">
                <div className="relative flex h-2.5 w-2.5 mt-0.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5B95FF] opacity-70"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2962FF] shadow-[0_0_8px_rgba(41,98,255,0.8)]"></span>
                </div>
                
                <h2 className="text-white font-bold text-base tracking-[0.25em] uppercase whitespace-nowrap drop-shadow-md">
                  Add Widget
                </h2>
                
                <div className="w-10 flex justify-start mt-0.5">
                  {(pendingVisible ? pendingVisible.size : targetSectionVisibleIds.size) > 0 && (
                    <span className="text-[#5B95FF] text-sm font-bold font-mono tracking-widest animate-in fade-in zoom-in duration-300 drop-shadow-sm">
                      [{(pendingVisible ? pendingVisible.size : targetSectionVisibleIds.size)}]
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 relative z-10">
                <button 
                  onClick={(e) => { e.stopPropagation(); enterPending([]); }}
                  disabled={(pendingVisible ? pendingVisible.size : targetSectionVisibleIds.size) === 0}
                  className="px-4 py-1.5 text-xs font-medium text-slate-400 hover:text-white border border-[#2A3143] hover:border-slate-500 hover:bg-white/5 rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A3143] disabled:cursor-not-allowed"
                >
                  CLEAR
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const selectionSet = pendingVisible ?? targetSectionVisibleIds;
                    const selectedIds = effectiveIds.filter((id) => selectionSet.has(id));

                    // Apply pending selections: either replace section widgets (preferred) or fallback to add/toggle.
                    if (onApplySectionWidgets && targetSectionId !== null && typeof targetSectionId !== 'undefined') {
                      onApplySectionWidgets(targetSectionId, selectedIds);
                    } else {
                      selectedIds.forEach(id => {
                        if (onSelectWidget) onSelectWidget(id, targetSectionId ?? null);
                        else onToggle(id);
                      });
                    }
                    if (controlled) onOpenChange?.(false); else setInternalOpen(false);
                  }}
                  className="px-5 py-1.5 text-xs font-medium text-white bg-gradient-to-b from-[#3B82F6] to-[#2563EB] hover:from-[#478BFF] hover:to-[#2962FF] border border-[#3B82F6]/50 rounded transition-all shadow-[0_2px_10px_rgba(37,99,235,0.3)] hover:shadow-[0_4px_15px_rgba(37,99,235,0.4)]"
                >
                  APPLY
                </button>
              </div>
            </div>

              <div className="p-5 shadow-[inset_0_4px_20px_rgba(0,0,0,0.2)] overflow-y-auto custom-scrollbar" style={{ maxHeight: (menuStyle.maxHeight && typeof menuStyle.maxHeight === 'number') ? menuStyle.maxHeight - 75 : 'calc(100vh - 10rem)' }}>
              <div className="grid grid-cols-6 gap-x-3 gap-y-0.5">
                {(() => {
                  const CATEGORY_COLORS: Record<string, any> = {
                    "Performance Analysis": { text: "text-blue-400", line: "from-blue-500/40 via-blue-900/10", pill: "bg-blue-500/10 border border-blue-500/20" },
                    "Averages": { text: "text-purple-400", line: "from-purple-500/40 via-purple-900/10", pill: "bg-purple-500/10 border border-purple-500/20" },
                    "Totals & Cumulative": { text: "text-emerald-400", line: "from-emerald-500/40 via-emerald-900/10", pill: "bg-emerald-500/10 border border-emerald-500/20" },
                    "Trade Statistics": { text: "text-amber-400", line: "from-amber-500/40 via-amber-900/10", pill: "bg-amber-500/10 border border-amber-500/20" },
                  };

                  return categories.map((catKey, index) => {
                    const arr = grouped.get(catKey)!.filter(a => a.id !== 'weekSummary').sort((a,b) => normalizeTitle(a.title || a.id).localeCompare(normalizeTitle(b.title || b.id)));
                    if (arr.length === 0) return null;
                    const theme = CATEGORY_COLORS[catKey] || CATEGORY_COLORS["Performance Analysis"];
                    
                    return (
                      <React.Fragment key={catKey}>
                        {/* Category Header */}
                        <div className={`col-span-full flex items-center justify-center gap-3 ${index > 0 ? 'mt-4' : 'mt-1'} mb-2 opacity-90`}>
                          <div className={`h-px flex-1 bg-gradient-to-l ${theme.line} to-transparent`}></div>
                          <h3 className={`${theme.text} ${theme.pill} px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest whitespace-nowrap shadow-sm backdrop-blur-sm`}>
                            {(WIDGET_CATEGORIES as any)[catKey] ?? catKey}
                          </h3>
                          <div className={`h-px flex-1 bg-gradient-to-r ${theme.line} to-transparent`}></div>
                        </div>

                        {arr.map(({ id, title }) => {
                          const isSelected = pendingVisible ? pendingVisible.has(id) : (targetSectionVisibleIds.has(id) || visibleIds.has(id));
                          const label = normalizeTitle(title || id);
                          return (
                            <label 
                              key={id} 
                              className={`relative flex items-center gap-2.5 py-1 px-1.5 -ml-1.5 rounded-md cursor-pointer group transition-all duration-200 overflow-hidden ${
                                isSelected ? 'bg-[#2962FF]/[0.08] shadow-[inset_0_0_0_1px_rgba(41,98,255,0.1)]' : 'hover:bg-white/[0.03]'
                              }`}
                              title={label}
                            >
                              <div className={`absolute left-0 top-0 bottom-0 w-0.5 bg-[#2962FF] transition-transform duration-200 origin-left ${isSelected ? 'scale-x-100' : 'scale-x-0'}`}></div>

                              <div className="relative flex items-center justify-center flex-shrink-0 ml-1">
                                <input 
                                  type="checkbox" 
                                  checked={isSelected}
                                  onChange={() => {
                                    const next = new Set(pendingVisible ? pendingVisible : targetSectionVisibleIds);
                                    if (next.has(id)) next.delete(id); else next.add(id);
                                    setPendingVisible(next);
                                  }}
                                  className="peer appearance-none w-3.5 h-3.5 border border-[#3A4359] rounded-[3px] bg-black/20 checked:bg-[#2563EB] checked:border-[#2563EB] cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2962FF]/40 focus:ring-offset-1 focus:ring-offset-[#0F131C] group-hover:border-slate-400 checked:group-hover:border-[#2563EB]"
                                />
                                <svg 
                                  className="absolute w-2.5 h-2.5 text-white pointer-events-none transition-all duration-200 scale-50 opacity-0 peer-checked:scale-100 peer-checked:opacity-100" 
                                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              </div>
                              <span className={`text-[12.5px] leading-tight truncate transition-colors duration-200 ${
                                isSelected ? 'text-[#7AA8FF] font-medium' : 'text-slate-300 group-hover:text-white'
                              }`}>
                                {label}
                              </span>
                            </label>
                          );
                        })}
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>
            </div>
          </div>
        ) : (
          <div ref={menuRef} role="menu" className={`fixed z-50 w-[880px] max-w-[96vw] max-h-[700px] rounded-xl shadow-xl border ${borderCol} ${panelBg} overflow-auto widget-card`} style={menuStyle}>
            {/* Header: Clear/Select left, Selected count right */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${borderCol}`}>
                  <div className="flex items-center gap-2">
                  <>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={handleClearAll}
                      className={`text-sm px-3 py-2 rounded-md border ${noneSelected ? 'border-blue-500 ' + (isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white') : borderCol + ' ' + (isDark ? 'bg-gray-900 hover:bg-gray-800 text-gray-200' : 'bg-white hover:bg-zinc-100 text-zinc-800')}`}
                    >Clear All</button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={handleSelectAll}
                      className={`text-sm px-3 py-2 rounded-md border ${allSelected ? 'border-blue-500 ' + (isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white') : 'border-blue-500 ' + (isDark ? 'bg-gray-900 hover:bg-gray-800 text-gray-200' : 'bg-white hover:bg-zinc-100 text-zinc-800')}`}
                    >Select All</button>
                  </>
              </div>
              <div className={`text-sm ${subtleText}`}>Selected: <b>{selectedCount}</b> / {items.length}</div>
            </div>

            {/* Favorites section (header + chips) */}
            <div className={`px-4 pt-3`}>
              <div className={`rounded-xl border ${isDark ? 'border-amber-400/30 bg-amber-500/10 backdrop-blur-[2px] shadow-amber-900/20' : 'border-amber-200 bg-amber-50'} px-3 py-2 mb-3`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Star className={`${isDark ? 'text-amber-300 drop-shadow-[0_0_6px_#fac304]' : 'text-amber-600'}`} size={16} />
                      <div className="text-sm font-medium">
                        Favorites <span className={subtleText}>{favorites.length ? `${favorites.length} pinned` : ''}</span>
                      </div>
                    </div>
                    <div className={`mt-0 text-xs ${subtleText}`}>
                      Pinned widgets are always shown at the top of your Dashboard for quick access.
                    </div>
                  </div>
                  <button
                    onClick={clearFavs}
                    className={`flex-shrink-0 ${favorites.length > 0 ? clearBtnClass : clearBtnClass + ' invisible pointer-events-none'}`}
                    aria-hidden={favorites.length === 0}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {favorites.length === 0 && (
                  <div className={`col-span-3 text-xs ${subtleText}`}>Click the ★ on any widget to pin it here.</div>
                )}
                {favorites.map(id => {
                  const def = (WIDGET_REGISTRY as any)[id];
                  if (!def) return null;
                  const on = pendingVisible ? pendingVisible.has(id) : visibleIds.has(id);
                  const normalized = normalizeTitle(def.title);
                  const Icon = ICON_MAP[id] || BarChart3;
                  return (
                    <button
                        key={id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (pendingVisible) {
                            const next = new Set(pendingVisible);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            setPendingVisible(next);
                            return;
                          }
                          if (onSelectWidget) {
                            onSelectWidget(id, targetSectionId ?? null);
                          } else {
                            onToggle(id);
                          }
                        }}
                        className={`flex items-center gap-3 px-4 h-[44px] rounded-xl border text-left text-xs transition-colors ${favChip(on)} ${isDark && on ? 'shadow-[0_0_8px_2px_#fac30433]' : ''}`}
                        title={normalized}
                      >
                      <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md ${on ? (isDark ? 'bg-amber-800 text-amber-100' : 'bg-amber-100 text-amber-700') : (isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-600')}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0 truncate whitespace-nowrap tracking-tight">{normalized}</span>
                    </button>
                  );
                })}
              </div>
              {/* Edge drop shadow under Favorites section (more subtle) */}
              <div className="relative z-10 h-3 -mb-1 pointer-events-none">
                <div className="h-full w-full rounded-b-xl" style={{background: 'linear-gradient(to bottom, rgba(0,0,0,0.10), rgba(0,0,0,0))'}} />
              </div>
            </div>

            {/* Categories */}
            <div className="px-4 pb-4">
                {categories.map((catKey, idx) => {
                const arr = grouped.get(catKey)!;
                const ids = arr.map(a => a.id);
                const selectedInCat = ids.filter(id => pendingVisible ? pendingVisible.has(id) : visibleIds.has(id)).length;
                const all = selectedInCat === ids.length && ids.length > 0;
                const catLabel = (WIDGET_CATEGORIES as any)[catKey] ?? catKey;
                const style = CATEGORY_STYLES[catKey] || { from: 'from-zinc-300/30', to: 'to-zinc-200/10', iconColor: 'text-zinc-500' };
                const desc = CATEGORY_DESCRIPTIONS[catKey] || '';

                const onSelectCat = () => ids.forEach(id => { if (!visibleIds.has(id)) onToggle(id); });
                const onClearCat = () => ids.forEach(id => { if (visibleIds.has(id)) onToggle(id); });

                return (
                  <section key={catKey} className="py-3">
                    {/* Category header with soft gradient and Select pill */}
                    <div className={`mb-4 rounded-xl border ${borderCol} bg-gradient-to-r ${style.from} ${style.to} px-3 py-2`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className={`text-sm font-semibold ${headerText}`}>{catLabel}</div>
                            <div className={`text-xs ${subtleText}`}>({selectedInCat}/{ids.length})</div>
                          </div>
                          {desc && <div className={`text-xs ${subtleText}`}>{desc}</div>}
                        </div>
                        <button
                          onClick={all ? onClearCat : onSelectCat}
                          className={`text-sm px-3 py-1.5 rounded-md border ${borderCol} ${isDark ? 'bg-gray-900 hover:bg-gray-800 text-gray-200' : 'bg-white hover:bg-zinc-100 text-zinc-800'}`}
                          title={all ? 'Clear Category' : 'Select Category'}
                        >
                          {all ? 'Clear' : 'Select'}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      {arr.map(({ id, title }) => {
                        const on = pendingVisible ? pendingVisible.has(id) : visibleIds.has(id);
                        const normalized = normalizeTitle(title);
                        const Icon = ICON_MAP[id] || BarChart3;
                        const desc = WIDGET_DESCRIPTIONS[id] || descFromCategory(catKey);
                        return (
                          <div key={id} className="relative">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (pendingVisible) {
                                  const next = new Set(pendingVisible);
                                  if (next.has(id)) next.delete(id);
                                  else next.add(id);
                                  setPendingVisible(next);
                                  return;
                                }
                                if (onSelectWidget) {
                                  onSelectWidget(id, targetSectionId ?? null);
                                } else {
                                  onToggle(id);
                                }
                              }}
                              className={`${on
                                ? (isDark ? 'border-blue-500 bg-blue-900/20 text-white' : 'border-blue-500 bg-blue-50')
                                : (isDark ? 'border-gray-700 bg-zinc-900 text-zinc-300' : 'border-zinc-300 bg-white text-zinc-900')
                              } w-full text-left px-4 py-2 h-[64px] rounded-xl border transition-colors`}
                              title={normalized}
                              aria-pressed={on}
                            >
                              <div className="flex items-start gap-3">
                                <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md ${on ? (isDark ? 'bg-blue-800 text-blue-100' : 'bg-blue-100 text-blue-700') : (isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-600')}`}>
                                  <Icon className="w-4 h-4" />
                                </span>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium tracking-tight text-zinc-800 dark:text-zinc-100 whitespace-nowrap truncate">{normalized}</div>
                                  <div className={`text-[11px] leading-snug truncate ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{desc}</div>
                                </div>
                              </div>
                            </button>
                            {favButtons(id)}
                          </div>
                        );
                      })}
                    </div>

                    {idx < categories.length - 1 && (
                      <div className={`mt-3 h-px ${isDark ? 'bg-gradient-to-r from-gray-800 via-gray-700/40 to-gray-800' : 'bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200'}`} />
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        )
      , document.body)}
    </div>
  );
}
